import { createAsyncThunk, isFulfilled, isPending } from "@reduxjs/toolkit";
import axios from "axios";
import { IPaginationResponse } from "shared/util/pagination.constants";
import {
  IEdoQuizAttempt,
  defaultValue
} from "../../model/edo-quiz-attempt.model";
import { cleanEntity } from "../../util/entity-utils";
import {
  EntityState,
  IQueryParamsWithFilters,
  createEntitySlice,
  serializeAxiosError
} from "../reducer.utils";

const initialState: EntityState<IEdoQuizAttempt> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = "/quiz-attempts";

// Actions

export const getEntities = createAsyncThunk(
  "edoQuizAttempt/fetch_entity_list",
  async ({ page, size, sort, filters }: IQueryParamsWithFilters) => {
    const params = new URLSearchParams(filters);
    if (sort) {
      if (page !== null && page !== undefined) params.append("page", String(page));
      if (size !== null && size !== undefined) params.append("limit", String(size));
      if (typeof sort === "string") {
        params.append("sortBy", sort.replace(",", ":"));
      } else {
        for (let s of sort) {
          params.append("sortBy", s.replace(",", ":"));
        }
      }
    }
    let requestUrl;
    if (params.toString() === "") {
      requestUrl = `${apiUrl}`;
    } else {
      requestUrl = `${apiUrl}?${params.toString()}`;
    }
    return axios.get<IPaginationResponse<IEdoQuizAttempt>>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  "edoQuizAttempt/fetch_entity",
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<{ data: IEdoQuizAttempt }>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  "edoQuizAttempt/create_entity",
  async (entity: IEdoQuizAttempt, thunkAPI) => {
    const result = await axios.post<{ data: IEdoQuizAttempt }>(
      apiUrl,
      cleanEntity(entity)
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  "edoQuizAttempt/update_entity",
  async (entity: IEdoQuizAttempt, thunkAPI) => {
    const result = await axios.put<{ data: IEdoQuizAttempt }>(
      `${apiUrl}/${entity.id}`,
      cleanEntity(entity)
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  "edoQuizAttempt/partial_update_entity",
  async (entity: IEdoQuizAttempt, thunkAPI) => {
    const result = await axios.patch<{ data: IEdoQuizAttempt }>(
      `${apiUrl}/${entity.id}`,
      cleanEntity(entity)
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  "edoQuizAttempt/delete_entity",
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IEdoQuizAttempt>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);
// slice

export const EdoQuizAttemptSlice = createEntitySlice({
  name: "edoQuizAttempt",
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data.data;
      })
      .addCase(deleteEntity.fulfilled, (state) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const {
          data: {
            data,
            meta: { totalItems },
          },
          headers,
        } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems,
        };
      })
      .addMatcher(
        isFulfilled(createEntity, updateEntity, partialUpdateEntity),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccess = true;
          state.entity = action.payload.data.data;
        }
      )
      .addMatcher(isPending(getEntities, getEntity), (state) => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(
          createEntity,
          updateEntity,
          partialUpdateEntity,
          deleteEntity
        ),
        (state) => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      );
  },
});

export const { reset } = EdoQuizAttemptSlice.actions;

// Reducer
export default EdoQuizAttemptSlice.reducer;
